/* eslint-disable max-lines-per-function */
import React, { useState } from "react";
import { gql } from "graphql-tag";

import Grid from "@material-ui/core/Grid";
import { useMutation } from "@apollo/client";
import { Tooltip } from "@material-ui/core";
import { Viewer } from "components/auth/types";
import { MoveInModal } from "pages/dashboard/interests-profile-slideout/components/modals/move-in";
import { InterestStage } from "model/interest/status";
import logger from "services/logger";
import { useSnackbar } from "components/haven-design/snackbar";

import { refetchHandles } from "data";
import { changeMultipleStatuses, changeMultipleStatusesVariables, ElasticQuery } from "types/gql-types";
import { useInterestsTable } from "../interests-table/table-context";
import { formatFilters, useSidebarFilters } from "../sidebar-filters";
import SelectInterestsButton from "./button-select-interests";

import { MessageModal } from "./group-message-modal";
import ChangeStatusModal from "./change-status-modal";


import CsvButton from "./button-csv";
import { MessageButton } from "./button-message";
import SearchBox from "./searchbox";
import RefreshButton from "./button-refresh";
import ActionButton from "./button-action";
import ButtonChangeStatus from "./button-change-status";
import NewInterestButton from "./button-new";
import styles from "./interest-toolbar.module.css";
import { DownloadFormModal } from "./download-form-modal";

const CHANGE_STATUS_QUERY = gql`
  mutation changeMultipleStatuses(
    $interestListIds: [ID!]!,
    $interestIds: [ID!],
    $elasticQuery: ElasticQuery,
    $status: String!,
    $reason: String,
    $pageFrom: Int,
    $pageTo: Int
  ) {
    changeInterestsStatus(
      interestListIds: $interestListIds
      interestIds: $interestIds
      elasticQuery: $elasticQuery
      pageFrom: $pageFrom
      pageTo: $pageTo
      status: $status
      reason: $reason
    )
  }
`;

const STATUSES_NEEDS_REASON = [InterestStage.Removed, InterestStage.MovedIn];

type ToolbarProps = {
  viewer: Viewer;
  interestListId: string;
  sendFullAppToInterestsEnabled: boolean;
  slideoutEnabled: boolean;
  weightedPrefsEnabled: boolean;
  page?: string;
  defaultSorting?: string;
};

const getScope = (
  withQuery: boolean,
  elasticQuery: ElasticQuery | null,
  interestIds: string[],
) => ({
  interestIds: withQuery ? null : interestIds,
  elasticQuery: (withQuery && elasticQuery) || null,
});

type SortingOptions = {
  [key: string]: {label: string}
}

const SORTING_OPTIONS: SortingOptions = {
  LAST_NAME: {
    label: "Last Name",
  },
  POSITION_NUMBER: {
    label: "Position",
  },
  SUBMIT_DATE_THEN_POSITION_NUMBER: {
    label: "Submit Date then Position Number",
  },
  SUBMIT_DATE_THEN_WEIGHTED_PREFERENCES: {
    label: "Submit Date then Weighted Preferences",
  },
  WEIGHTED_PREFERENCES_THEN_SUBMIT_DATE: {
    label: "Weighted Preferences then Submit Date",
  },
  WEIGHTED_PREFERENCES_THEN_POSITION_NUMBER: {
    label: "Weighted Preferences then Position Number",
  },
};

const INSTANT_PROCESSING_THRESHOLD = 800;

const getIndexNameBySortingKey = (indexKey:string | undefined) => (indexKey ? SORTING_OPTIONS[indexKey]?.label : "default");


const InterestsToolbar: React.FC<ToolbarProps> = (props) => {
  const { openSnackbar } = useSnackbar();
  const [messageModalOpen, setMessageModalOpen] = useState(false);
  const [showCSVModal, setShowCSVModal] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<null | string>(null);
  const [changeStatus, { loading: statusChangeLoading }] =
    useMutation<changeMultipleStatuses, changeMultipleStatusesVariables>(CHANGE_STATUS_QUERY);

  const {
    selectedInterestIds,
    tableResults,
    deselectAllInterests,
    hasSelectedAllInterests,
    totalSelectedInterests,
    selectedPageRange,
  } = useInterestsTable();
  const { search } = useSidebarFilters();
  const selectedIds = selectedInterestIds.slice();
  const selectedNames = tableResults.filter((x) => selectedIds.includes(x.id));
  const {
    viewer,
    interestListId,
    sendFullAppToInterestsEnabled,
    slideoutEnabled,
  } = props;
  const {
    filterState,
    textFilter,
    interestListIds,
  } = useSidebarFilters();
  const elasticQuery = {
    filters: formatFilters(filterState),
    textFilter,
    defaultSorting: props.defaultSorting,
  };


  const onStatusChange = (status: string, inputReason: string | null) => {
    if (STATUSES_NEEDS_REASON.includes(status as InterestStage) && inputReason === null) {
      setSelectedStatus(status);
      return new Promise((resolve) => {
        resolve("");
      });
    }
    const onResetState = () => setSelectedStatus(null);

    const onChangeStatusSuccess = () => {
      onResetState();
      if (totalSelectedInterests > INSTANT_PROCESSING_THRESHOLD) {
        openSnackbar({
          message: "The operation been added to a queue. The changes will be reflected in the dashboard soon.",
          duration: 6000,
          status: "success",
        });
      } else {
        openSnackbar({ message: "Success", duration: 2000, status: "success" });
      }
      refetchHandles.pmInterests();
      search();
      deselectAllInterests();
    };

    const onChangeStatusFailure = (err: Error) => {
      onResetState();
      openSnackbar({
        message: "Server failure: please contact support at support@havenconnect.com if the problem persists.",
        duration: 5000,
        status: "error",
      });
      logger.sendError(err);
    };

    const { from, to } = selectedPageRange;
    const selectedAll = hasSelectedAllInterests;
    const selectedPages = !(from === null && to === null);
    const selectedForQuery = selectedAll || selectedPages;
    const variables = {
      interestListIds,
      pageFrom: selectedPages ? from : null,
      pageTo: selectedPages ? to : null,
      status,
      reason: inputReason,
      ...getScope(selectedForQuery, elasticQuery, selectedIds),
    };

    const request = changeStatus({ variables });
    openSnackbar({ message: "Processing...", duration: 2000, status: "alert" });
    request.then(onChangeStatusSuccess);
    request.catch(onChangeStatusFailure);

    return request;
  };

  return (
    <Grid container spacing={1}>
      <Grid item>
        <SelectInterestsButton isChecked={false} />
      </Grid>
      {slideoutEnabled ? (
        <Grid item>
          <ActionButton
            setShowCSVModal={setShowCSVModal}
            setMessageModalOpen={setMessageModalOpen}
            onStatusChange={onStatusChange}
            sendFullAppToInterestsEnabled={sendFullAppToInterestsEnabled}
            selectedNames={selectedNames}
            interestListId={interestListId}
            viewer={viewer}
            statusChangeLoading={statusChangeLoading}
          />
        </Grid>
      ) : (<>
        <Tooltip title={(totalSelectedInterests > 0) ? "Change Status" : "Select Applications First"}>
          <Grid item>
            <ButtonChangeStatus
              onStatusChange={onStatusChange}
              isAvailable={totalSelectedInterests > 0}
              loading={statusChangeLoading}
            />
          </Grid>
        </Tooltip>
        <Tooltip title="Export">
          <Grid item>
            <CsvButton setShowCSVModal={setShowCSVModal} viewer={viewer} />
          </Grid>
        </Tooltip>
        {interestListIds.length === 1 && (
          <Tooltip title="Send Message">
            <Grid item>
              <MessageButton
                setMessageModalOpen={setMessageModalOpen}
                viewer={viewer}
                recipients={selectedNames}
                currentListId={interestListId}
              />
            </Grid>
          </Tooltip>
        )}
      </>
      )}
      <Grid item style={{ flexGrow: 1 }}>
        <SearchBox />
      </Grid>
      <Tooltip title="Add Application">
        <Grid item>
          <NewInterestButton currentListId={interestListId} />
        </Grid>
      </Tooltip>
      <Tooltip title="Refresh">
        <Grid item>
          <RefreshButton />
        </Grid>
      </Tooltip>
      <Grid item>
        <div className={styles.sortCriteria}>
          Sorted by {getIndexNameBySortingKey(props.defaultSorting)}
        </div>
      </Grid>
      <MessageModal
        viewer={viewer}
        isOpen={messageModalOpen}
        recipients={selectedNames}
        onRequestClose={() => setMessageModalOpen(false)}
        currentListId={interestListId}
      />
      <MoveInModal
        isOpen={selectedStatus === "Moved In"}
        onRequestClose={() => setSelectedStatus(null)}
        interestIds={selectedIds}
        interestListId={interestListId}
      />
      <ChangeStatusModal
        selectedStatus={selectedStatus}
        setSelectedStatus={setSelectedStatus}
        onStatusChange={onStatusChange}
        loading={statusChangeLoading}
      />

      <DownloadFormModal
        isOpen={showCSVModal}
        formIds={selectedInterestIds.slice()}
        allForms={hasSelectedAllInterests}
        interestListId={interestListId}
        defaultSorting={props.defaultSorting}
        onRequestClose={() => setShowCSVModal(false)}
      />
    </Grid>
  );
};

export default InterestsToolbar;

